import { computed, ref } from "vue"
import {
  type BiometricAuthResult,
  type BiometricStatus,
  BiometryType,
  biometricService,
} from "../services/biometricService"
import { logger } from "../utils/logger"
import { safeSetItem } from "../utils/storageQuota"

// SECURITY NOTE: this is a UX convenience gate, NOT a data-security boundary.
// The lock is a client-side reactive overlay whose state lives in localStorage;
// it does not clear or invalidate the Supabase session, so it never actually
// gates API/data access, and on web it can be bypassed via devtools/localStorage.
// Real protection is server-side auth (the JWT). Do not present this as a
// control that protects the user's financial data at rest. If a true lock is
// ever required, lock() must drop the in-memory session and force re-auth
// (token refresh) on unlock.

// Storage key for biometric preferences
const BIOMETRIC_ENABLED_KEY = "budget_app_biometric_enabled"
const BIOMETRIC_LAST_AUTH_KEY = "budget_app_biometric_last_auth"

// State
const isBiometricEnabled = ref(false)
const isBiometricAvailable = ref(false)
const biometryType = ref<BiometryType>(BiometryType.none)
const biometryTypeName = ref("")
const isLocked = ref(false)
const isChecking = ref(false)
const isInitializing = ref(true) // Track initial load vs unlock operations
const lastAuthTime = ref<number | null>(null)

// Lock timeout in milliseconds (5 minutes)
const LOCK_TIMEOUT = 5 * 60 * 1000

/**
 * Load biometric preferences from localStorage. Wrapped in try/catch so that
 * private/incognito browsing modes (where storage access throws) don't crash
 * app startup — biometrics will simply default to off.
 */
function loadPreferences() {
  try {
    const enabled = localStorage.getItem(BIOMETRIC_ENABLED_KEY)
    isBiometricEnabled.value = enabled === "true"

    const lastAuth = localStorage.getItem(BIOMETRIC_LAST_AUTH_KEY)
    if (lastAuth) {
      const parsed = parseInt(lastAuth, 10)
      lastAuthTime.value = Number.isFinite(parsed) ? parsed : null
    }
  } catch (error) {
    logger.warn("Could not load biometric preferences", {
      error: error instanceof Error ? error.message : String(error),
    })
    isBiometricEnabled.value = false
    lastAuthTime.value = null
  }
}

/**
 * Save biometric preferences to localStorage. Quota errors are logged but
 * non-fatal — biometric prefs are user-recoverable on next toggle.
 */
function savePreferences() {
  try {
    safeSetItem(BIOMETRIC_ENABLED_KEY, String(isBiometricEnabled.value))
    if (lastAuthTime.value) {
      safeSetItem(BIOMETRIC_LAST_AUTH_KEY, String(lastAuthTime.value))
    }
  } catch (error) {
    logger.warn("Failed to persist biometric preferences", {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/**
 * Initialize the biometric store
 */
async function initBiometricStore(): Promise<void> {
  isInitializing.value = true
  isChecking.value = true

  try {
    // Load saved preferences FIRST - this determines if lock screen should show immediately
    loadPreferences()

    // If biometric is enabled, assume locked until we verify otherwise
    // This ensures the lock screen shows immediately, hiding app content
    if (isBiometricEnabled.value) {
      const shouldLock = checkShouldLock()
      isLocked.value = shouldLock
    }

    // Now check device biometric capabilities (async operation)
    const status: BiometricStatus = await biometricService.checkBiometryAvailability()
    isBiometricAvailable.value = status.isAvailable
    biometryType.value = status.biometryType
    biometryTypeName.value = biometricService.getBiometryTypeName(status.biometryType)

    // If biometrics are not available, disable the feature
    if (!status.isAvailable) {
      isBiometricEnabled.value = false
      isLocked.value = false
      savePreferences()
    }
  } finally {
    isChecking.value = false
    isInitializing.value = false
  }
}

/**
 * Check if the app should be locked based on last auth time
 */
function checkShouldLock(): boolean {
  if (!isBiometricEnabled.value) {
    return false
  }

  if (!lastAuthTime.value) {
    return true
  }

  const now = Date.now()
  const elapsed = now - lastAuthTime.value

  return elapsed > LOCK_TIMEOUT
}

/**
 * Enable biometric authentication
 */
async function enableBiometric(): Promise<BiometricAuthResult> {
  // First verify biometrics work by authenticating
  const result = await biometricService.authenticate("Enable biometric unlock")

  if (result.success) {
    isBiometricEnabled.value = true
    lastAuthTime.value = Date.now()
    isLocked.value = false
    savePreferences()
  }

  return result
}

/**
 * Disable biometric authentication
 */
function disableBiometric(): void {
  isBiometricEnabled.value = false
  isLocked.value = false
  lastAuthTime.value = null
  localStorage.removeItem(BIOMETRIC_LAST_AUTH_KEY)
  savePreferences()
}

/**
 * Attempt to unlock the app using biometrics
 */
async function unlock(): Promise<BiometricAuthResult> {
  if (!isBiometricEnabled.value) {
    isLocked.value = false
    return { success: true }
  }

  isChecking.value = true

  try {
    const result = await biometricService.authenticate()

    if (result.success) {
      lastAuthTime.value = Date.now()
      isLocked.value = false
      savePreferences()
    }

    return result
  } finally {
    isChecking.value = false
  }
}

/**
 * Lock the app manually
 */
function lock(): void {
  if (isBiometricEnabled.value) {
    isLocked.value = true
    lastAuthTime.value = null
    localStorage.removeItem(BIOMETRIC_LAST_AUTH_KEY)
  }
}

/**
 * Record successful authentication (called after regular login)
 */
function recordAuthentication(): void {
  if (isBiometricEnabled.value) {
    lastAuthTime.value = Date.now()
    isLocked.value = false
    savePreferences()
  }
}

/**
 * Handle app resume from background
 */
function handleAppResume(): void {
  if (isBiometricEnabled.value && checkShouldLock()) {
    isLocked.value = true
  }
}

// Computed values
const shouldShowLockScreen = computed(() => {
  // Show lock screen immediately when biometrics are enabled and locked
  // Don't wait for isChecking to complete - this ensures app content is hidden
  return isBiometricEnabled.value && isLocked.value
})

const biometricStatusText = computed(() => {
  if (!isBiometricAvailable.value) {
    return "Not available on this device"
  }
  return biometryTypeName.value
})

export {
  // State
  isBiometricEnabled,
  isBiometricAvailable,
  biometryType,
  biometryTypeName,
  isLocked,
  isChecking,
  isInitializing,
  // Computed
  shouldShowLockScreen,
  biometricStatusText,
  // Actions
  initBiometricStore,
  enableBiometric,
  disableBiometric,
  unlock,
  lock,
  recordAuthentication,
  handleAppResume,
}
